import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAppEntryRedirect } from '../hooks/useAppEntryRedirect';
import { appendLobbyParams } from '@/utils/lobbyNavigation';
// import RulesView from './RulesView';

/** Preserve lobby deep-link params (from=lobby, token) when redirecting to /play. */
function buildPlayQuery(
  contestId: string,
  incoming: URLSearchParams,
  options?: { mode?: string }
): string {
  const params = new URLSearchParams();
  params.set('contestId', contestId);
  if (options?.mode) params.set('mode', options.mode);
  if (incoming.get('from') === 'lobby') params.set('from', 'lobby');
  const lobbyReturn = incoming.get('lobbyReturn');
  if (lobbyReturn) params.set('lobbyReturn', lobbyReturn);
  const token = incoming.get('token');
  if (token) params.set('token', token);
  return params.toString();
}

/**
 * App entry point. Redirects to PlayView (a pending contest the user has not entered,
 * else their most upcoming pending or active one) or to ResultsView.
 *
 * The full-page RulesView entry step is bypassed for now (see the commented block
 * below and in useAppEntryRedirect), so `showRulesView` is never true.
 */
export default function AppEntry() {
  const { gameId } = useParams<{ gameId?: string }>();
  const [searchParams] = useSearchParams();
  const { result, isLoading, error } = useAppEntryRedirect({ enabled: !gameId });

  if (gameId) {
    return <Navigate to={`/play?${buildPlayQuery(gameId, searchParams)}`} replace />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#121212]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-[#53d337]" />
          <p className="text-zinc-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#121212] px-4">
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 max-w-md">
          <p className="text-red-400 font-semibold">Error loading</p>
          <p className="text-red-300 text-sm mt-1">
            {error instanceof Error ? error.message : 'Something went wrong'}
          </p>
        </div>
      </div>
    );
  }

  if (!result) return null;

  if (!result.showRulesView && result.redirectTo.path === '/play') {
    const { contestId, mode } = result.redirectTo;
    return (
      <Navigate
        to={`/play?${buildPlayQuery(contestId, searchParams, mode === 'results' ? { mode: 'results' } : undefined)}`}
        replace
      />
    );
  }

  if (!result.showRulesView && result.redirectTo.path === '/results') {
    return <Navigate to={appendLobbyParams('/results', searchParams)} replace />;
  }

  // TEMPORARILY BYPASSED: skip RulesView, go straight to the game
  // if (result.showRulesView && result.mostRecentPendingContestId) {
  //   return (
  //     <RulesView
  //       contestId={result.mostRecentPendingContestId}
  //       variant="entry"
  //       onPlayNow={id => navigate(`/play?contestId=${id}`)}
  //     />
  //   );
  // }
  //
  // if (result.showRulesView && !result.mostRecentPendingContestId) {
  //   return <Navigate to="/results" replace />;
  // }

  return null;
}
